"use client";

import {
  lazy,
  Suspense,
  useEffect,
  useState,
  useRef,
  useCallback,
  memo,
  useMemo,
} from "react";
import { useLoading } from "@/components/providers/LoadingProvider";

// Import widget components
const ImageGalleryWidget = lazy(
  () => import("./widgets/media/ImageGalleryWidget")
);
const BusinessHoursWidget = lazy(
  () => import("./widgets/business/BusinessHoursWidget")
);
const RichTextWidget = lazy(() => import("./widgets/content/RichTextWidget"));
const LinkWidget = lazy(() => import("./widgets/links/LinkWidget"));
const BioWidget = lazy(() => import("./widgets/bio/BioWidget"));
const SingleMediaWidget = lazy(
  () => import("./widgets/media/SingleMediaWidget")
);
const FileWidget = lazy(() => import("./widgets/media/FileWidget"));
const PlaceholderWidget = lazy(
  () => import("@/components/widgets/system/PlaceholderWidget")
);
const DividerWidget = lazy(() => import("./widgets/layout/DividerWidget"));
const MiniWidget = lazy(() => import("./widgets/links/MiniWidget"));
const VideoPlayerWidget = lazy(
  () => import("./widgets/media/VideoPlayerWidget")
);
const SpotifyWidget = lazy(() => import("./widgets/music/SpotifyWidget"));
const AppleMusicWidget = lazy(() => import("./widgets/music/AppleMusicWidget"));
const CalendlyWidget = lazy(() => import("./widgets/business/CalendlyWidget"));
const OpenTableWidget = lazy(
  () => import("./widgets/business/OpenTableWidget")
);
const MapWidget = lazy(() => import("./widgets/location/MapWidget"));
const PhotoAlbumWidget = lazy(() => import("./widgets/media/PhotoAlbumWidget"));

// Widget type to component mapping
const widgetComponents = {
  image_gallery: ImageGalleryWidget,
  business_hours: BusinessHoursWidget,
  rich_text: RichTextWidget,
  links: LinkWidget,
  bio: BioWidget,
  single_media: SingleMediaWidget,
  file: FileWidget,
  placeholder: PlaceholderWidget,
  divider: DividerWidget,
  mini_link: MiniWidget,
  video_player: VideoPlayerWidget,
  spotify: SpotifyWidget,
  apple_music: AppleMusicWidget,
  calendly: CalendlyWidget,
  opentable: OpenTableWidget,
  map: MapWidget,
  photo_album: PhotoAlbumWidget,
};

// Set default configuration and widget type properties for file widget
// This ensures the config button shows up in EditableWidget
export const fileWidgetDefaults = {
  type: "file",
  name: "File Document",
  description: "Upload and display document files",
  config_enabled: true,
  settings_enabled: true,
  default_config: {
    file: {
      showPreview: true,
    },
  },
  default_settings: {
    backgroundColor: "#1A1A1A",
    borderRadius: "1.5rem",
    transparency: 100,
    fontColor: "#FFFFFF",
  },
};

// Add default configuration and widget type properties for video player widget
export const videoPlayerWidgetDefaults = {
  type: "video_player",
  name: "Video Player",
  description: "Embed and play videos from YouTube",
  config_enabled: true,
  settings_enabled: true,
  default_config: {
    videoUrl: "",
    videoId: "",
    autoplay: false,
    muted: true,
    source: "youtube",
    embedCode: "",
  },
  default_settings: {
    backgroundColor: "#000000",
    borderRadius: "1.5rem",
    transparency: 100,
    fontColor: "#FFFFFF",
    padding: "0", // Explicitly set to 0 for no padding
  },
};

// Add default configuration and widget type properties for Spotify widget
export const spotifyWidgetDefaults = {
  type: "spotify",
  name: "Spotify",
  description: "Embed Spotify tracks, playlists, albums, and artists",
  config_enabled: true,
  settings_enabled: true,
  mini: true, // 2x2
  wide: true, // 4x2
  tall: true, // 2x4
  standard: true, // 4x4
  super_tall: true, // 4x6
  super_wide: true, // 8x4
  skinny: false, // 4x1 - not suitable for Spotify embeds
  default_config: {
    spotifyUrl: "",
    showLiveListening: false,
    isAuthenticated: false,
    spotifyAccessToken: "",
    refreshToken: "",
    userProfile: null,
    fullBackgroundArt: false, // New option to control full background art
  },
  default_settings: {
    backgroundColor: "#FFFFFF",
    borderRadius: "1.5rem",
    transparency: 100,
    fontColor: "#000000",
    padding: "0",
  },
};

// Add default configuration and widget type properties for Apple Music widget
export const appleMusicWidgetDefaults = {
  type: "apple_music",
  name: "Apple Music",
  description: "Embed Apple Music songs, albums, playlists, and artists",
  config_enabled: true,
  settings_enabled: true,
  mini: true, // 2x2
  wide: true, // 4x2
  tall: true, // 2x4
  standard: true, // 4x4
  super_tall: true, // 4x6
  super_wide: true, // 8x4
  skinny: false, // 4x1 - not suitable for Apple Music embeds
  default_config: {
    appleMusicUrl: "",
    showLiveListening: false,
    isAuthenticated: false,
    userProfile: null,
  },
  default_settings: {
    backgroundColor: "#FFFFFF",
    borderRadius: "1.5rem",
    transparency: 100,
    fontColor: "#000000",
    padding: "0",
  },
};

// Add default configuration and widget type properties for Calendly widget
export const calendlyWidgetDefaults = {
  type: "calendly",
  name: "Calendly",
  description: "Embed Calendly scheduling widget for appointment booking",
  config_enabled: true,
  settings_enabled: true,
  mini: false, // 2x2 - too small for Calendly
  wide: false, // 4x2 - too short for Calendly
  tall: true, // 2x4
  standard: true, // 4x4
  super_tall: true, // 4x6
  super_wide: true, // 8x4
  skinny: false, // 4x1 - not suitable for Calendly
  default_config: {
    calendlyUrl: "",
    hideEventTypeDetails: false,
    hideLandingPageDetails: false,
  },
  default_settings: {
    backgroundColor: "#FFFFFF",
    borderRadius: "1.5rem",
    transparency: 100,
    fontColor: "#000000",
    padding: "0",
  },
};

// Add default configuration and widget type properties for OpenTable widget
export const openTableWidgetDefaults = {
  type: "opentable",
  name: "OpenTable",
  description: "Enable restaurant reservations through OpenTable integration",
  config_enabled: true,
  settings_enabled: true,
  mini: true, // 2x2 - good for compact reservation button
  wide: true, // 4x2 - good for horizontal layout
  tall: true, // 2x4 - good for detailed info
  standard: true, // 4x4 - perfect for full info display
  super_tall: false, // 4x6 - not necessary for OpenTable
  super_wide: false, // 8x4 - too wide for most use cases
  skinny: false, // 4x1 - too small for meaningful info
  default_config: {
    openTableUrl: "",
    restaurantName: "",
    cuisineType: "",
    location: "",
    description: "",
    showDescription: true,
    showCuisine: true,
    showLocation: true,
    buttonText: "Make Reservation",
    buttonStyle: "primary",
  },
  default_settings: {
    backgroundColor: "#FFFFFF",
    borderRadius: "1.5rem",
    transparency: 100,
    fontColor: "#1F2937",
    padding: "1.5rem",
  },
};

// Add default configuration and widget type properties for Map widget
export const mapWidgetDefaults = {
  type: "map",
  name: "Map",
  description: "Display interactive maps with custom locations and styling",
  config_enabled: true,
  settings_enabled: true,
  mini: true, // 2x2 - good for small location reference
  wide: true, // 4x2 - good for horizontal layout
  tall: true, // 2x4 - good for detailed maps
  standard: true, // 4x4 - perfect for interactive maps
  super_tall: true, // 4x6 - great for detailed area views
  super_wide: true, // 8x4 - excellent for panoramic maps
  skinny: false, // 4x1 - too small for meaningful maps
  default_config: {
    latitude: null, // No default location to show empty state
    longitude: null, // No default location to show empty state
    zoom: 10,
    style: "light-v10", // Use light mode style as default
    darkModeStyle: "dark-v11", // Use default Mapbox dark style
    lightModeStyle: "light-v10", // Light mode map style
    showMarker: true,
    markerColor: "#007AFF", // iOS blue color
    showLabel: true, // Default to showing label
    labelText: "", // Will default to address when geocoded
    address: "", // Store the geocoded address
    isDarkMode: false, // Default to light mode
  },
  default_settings: {
    backgroundColor: "transparent",
    borderRadius: "1.5rem",
    transparency: 100,
    fontColor: "#000000",
    padding: "0", // Edge-to-edge like single_media
  },
};

// Add default configuration and widget type properties for Photo Album widget
export const photoAlbumWidgetDefaults = {
  type: "photo_album",
  name: "Photo Album",
  description:
    "Display photos in a polaroid-style album layout with overlapping effects",
  config_enabled: true,
  settings_enabled: true,
  mini: false, // 2x2 - too small for photo album
  wide: true, // 4x2 - good for compact album
  tall: true, // 2x4 - good for vertical album
  standard: true, // 4x4 - perfect for photo album
  super_tall: true, // 4x6 - great for large album display
  super_wide: true, // 8x4 - excellent for panoramic album
  skinny: false, // 4x1 - too small for meaningful album
  default_config: {
    title: "",
    images: [],
    showTitle: true, // Default to showing title
  },
  default_settings: {
    backgroundColor: "#0D0D11", // Default to dark-deeper for glass effect
    borderRadius: "1.5rem",
    transparency: 90, // 90% opacity for glass effect
    fontColor: "#F8FAFC", // neutral-light for white text
    padding: "1.5rem",
    // Keep these in settings for backwards compatibility
    showCaptions: true,
    enableLightbox: true,
  },
};

// Add a new WidgetSkeleton component
function WidgetSkeleton() {
  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="animate-pulse w-full h-full bg-gray-200/20 rounded-lg flex items-center justify-center">
        <div className="text-gray-400 text-sm">Loading widget...</div>
      </div>
    </div>
  );
}

// Memoize DynamicWidget to prevent unnecessary re-renders
const DynamicWidget = memo(function DynamicWidget({
  layout,
  config,
  widget,
  isEditing,
  spaceSlug,
  handleWidgetUpdate,
  spaceData,
  previewMode = "desktop",
  onDelete,
}) {
  const { setIsLoading } = useLoading();
  const [dimensions, setDimensions] = useState(null);
  const widgetRef = useRef(null);
  const resizeObserver = useRef(null);

  // Memoize enhanced widget type to prevent re-renders
  const enhancedWidget = useMemo(() => {
    if (!widget || !widget.widget_type) return widget;

    const widgetType = widget.widget_type.type;
    let enhancedWidgetType = { ...widget.widget_type };

    switch (widgetType) {
      case "file":
        enhancedWidgetType = {
          ...enhancedWidgetType,
          config_enabled: true,
          settings_enabled: true,
          default_config: fileWidgetDefaults.default_config,
        };
        break;
      case "video_player":
        enhancedWidgetType = {
          ...enhancedWidgetType,
          config_enabled: true,
          settings_enabled: true,
          default_config: videoPlayerWidgetDefaults.default_config,
        };
        break;
      case "spotify":
        enhancedWidgetType = {
          ...enhancedWidgetType,
          config_enabled: true,
          settings_enabled: true,
          default_config: spotifyWidgetDefaults.default_config,
        };
        break;
      case "apple_music":
        enhancedWidgetType = {
          ...enhancedWidgetType,
          config_enabled: true,
          settings_enabled: true,
          default_config: appleMusicWidgetDefaults.default_config,
        };
        break;
      case "calendly":
        enhancedWidgetType = {
          ...enhancedWidgetType,
          config_enabled: true,
          settings_enabled: true,
          default_config: calendlyWidgetDefaults.default_config,
        };
        break;
      case "opentable":
        enhancedWidgetType = {
          ...enhancedWidgetType,
          config_enabled: true,
          settings_enabled: true,
          default_config: openTableWidgetDefaults.default_config,
        };
        break;
      case "map":
        enhancedWidgetType = {
          ...enhancedWidgetType,
          config_enabled: true,
          settings_enabled: true,
          default_config: mapWidgetDefaults.default_config,
        };
        break;
      case "photo_album":
        enhancedWidgetType = {
          ...enhancedWidgetType,
          config_enabled: true,
          settings_enabled: true,
          default_config: photoAlbumWidgetDefaults.default_config,
        };
        break;
    }

    return {
      ...widget,
      widget_type: enhancedWidgetType,
    };
  }, [widget]);

  // Calculate dimensions based on container width and layout settings
  const calculateDimensions = useCallback(() => {
    if (!widgetRef.current) return;

    const containerWidth = widgetRef.current.offsetWidth;
    if (!containerWidth) return;

    const columns = layout?.[previewMode]?.columns || 4;
    const columnHeight = layout?.[previewMode]?.columnHeight || columns;
    const gapPixels = 24; // Match the gap from MasonryLayout

    // Calculate the width of a single column (accounting for gaps)
    const totalGapWidth = gapPixels * (columns - 1);
    const usableWidth = containerWidth - totalGapWidth;
    const columnWidth = usableWidth / columns;

    // Calculate width including gaps for the widget's columns
    const width = containerWidth;

    // Calculate height based on column units
    // Each column unit is a square, so we use columnWidth as the base unit
    const baseColumnHeight = columnWidth; // Each column unit is square
    const totalGapHeight = gapPixels * (columnHeight - 1);
    let height = baseColumnHeight * columnHeight + totalGapHeight;

    // Set minimum height for rich text widgets in skinny mode
    if (
      enhancedWidget?.widget_type?.type === "rich_text" &&
      columnHeight === 1
    ) {
      height = Math.max(height, 79.33);
    }

    setDimensions({ width, height });
  }, [layout, previewMode, enhancedWidget?.widget_type?.type]);

  // Setup ResizeObserver
  useEffect(() => {
    if (!widgetRef.current) return;

    // Initial calculation
    calculateDimensions();

    // Setup ResizeObserver
    resizeObserver.current = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect.width > 0) {
          calculateDimensions();
        }
      }
    });

    resizeObserver.current.observe(widgetRef.current);

    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, [calculateDimensions]);

  // Handle window resize
  useEffect(() => {
    window.addEventListener("resize", calculateDimensions);
    return () => window.removeEventListener("resize", calculateDimensions);
  }, [calculateDimensions]);

  // Recalculate on layout or preview mode change
  useEffect(() => {
    calculateDimensions();
  }, [layout, previewMode, calculateDimensions]);

  useEffect(() => {
    setIsLoading(false);
  }, [setIsLoading]);

  // Add validation for widget prop
  if (!enhancedWidget || !enhancedWidget.widget_type.type) {
    console.warn("[DynamicWidget] Invalid widget data:", enhancedWidget);
    return null;
  }

  const WidgetComponent = widgetComponents[enhancedWidget.widget_type.type];

  if (!WidgetComponent) {
    console.warn(
      `[DynamicWidget] Widget type "${enhancedWidget.widget_type.type}" not found`
    );
    return null;
  }

  // Check for either draft space_id or published space id
  if (!spaceData?.space_data?.space_id && !spaceData?.space_data?.id) {
    console.warn("[DynamicWidget] No space ID provided to DynamicWidget");
    return null;
  }

  // Determine widget size based on column ratios
  const columns = layout?.[previewMode]?.columns || 4;
  const columnHeight = layout?.[previewMode]?.columnHeight || columns;
  let size = "standard";

  if (columns === 2 && columnHeight === 2) {
    size = "mini";
  } else if (columns === 4 && columnHeight === 2) {
    size = "wide";
  } else if (columns === 2 && columnHeight === 4) {
    size = "tall";
  } else if (columns === 8 && columnHeight === 4) {
    size = "super-wide";
  } else if (columns === 4 && columnHeight === 8) {
    size = "super-tall";
  }

  return (
    <>
      <Suspense fallback={<WidgetSkeleton />}>
        <div
          ref={widgetRef}
          className={`w-full h-full ${
            enhancedWidget.widget_type.type === "image_gallery" ||
            enhancedWidget.widget_type.type === "rich_text" ||
            enhancedWidget.widget_type.type === "single_media" ||
            enhancedWidget.widget_type.type === "video_player" ||
            enhancedWidget.widget_type.type === "map" ||
            enhancedWidget.widget_type.type === "photo_album"
              ? "overflow-visible" // Allow widgets with their own scrolling to handle it internally
              : "overflow-hidden content-constraint"
          }`}
          style={{
            height: "100%",
            width: "100%",
            background: "transparent",
            boxShadow: "none",
            border: "none",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            padding:
              enhancedWidget.widget_type.type === "image_gallery" ||
              enhancedWidget.widget_type.type === "video_player" ||
              enhancedWidget.widget_type.type === "map" ||
              enhancedWidget.widget_type.type === "photo_album"
                ? 0
                : undefined,
          }}
        >
          <style jsx>{`
            .content-constraint {
              position: relative;
            }

            .content-constraint .truncated-text-container {
              max-height: 100%;
              overflow: hidden;
            }
          `}</style>

          <WidgetComponent
            type={enhancedWidget.widget_type.type}
            layout={layout}
            settings={layout.settings}
            config={config}
            isEditing={isEditing}
            handleWidgetUpdate={
              handleWidgetUpdate ||
              ((id, updates) => {
                console.warn(
                  "handleWidgetUpdate not provided to DynamicWidget",
                  id,
                  updates
                );
              })
            }
            onUpdate={(updates) => {
              // This method is called by some widgets like ImageGalleryWidget
              // Forward the update to parent handleWidgetUpdate if available
              if (handleWidgetUpdate && enhancedWidget?.id) {
                handleWidgetUpdate(enhancedWidget.id, updates);
              }
            }}
            spaceSlug={spaceSlug}
            spaceData={spaceData}
            widgetId={enhancedWidget.id}
            columns={columns}
            size={size}
            dimensions={dimensions}
            previewMode={previewMode}
            onDelete={onDelete && (() => onDelete(enhancedWidget.id))}
          />
        </div>
      </Suspense>
    </>
  );
});

DynamicWidget.displayName = "DynamicWidget";

export default DynamicWidget;
